/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { Redirect, RouteComponentProps, Switch } from 'react-router';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { routeTable } from 'src/core/utils/routeTable';
import { PrebuiltDomainsPage } from 'src/luis/appShell/buildPane/domainsPage/PrebuiltDomainsPage';
import { EntitiesPage } from 'src/luis/appShell/buildPane/entitiesPage/EntitiesPage';
import { EntityPage } from 'src/luis/appShell/buildPane/EntityPage';
import { FeaturesPage } from 'src/luis/appShell/buildPane/featuresPage/FeaturesPage';
import { IntentPage } from 'src/luis/appShell/buildPane/intentPage/IntentPage';
import { IntentsPage } from 'src/luis/appShell/buildPane/intentsPage/IntentsPage';
import { PatternsPage } from 'src/luis/appShell/buildPane/patternsPage/PatternsPage';
import { ReviewEndpointUtterancesPage } from 'src/luis/appShell/buildPane/reviewEndpointPage/ReviewEndpointUtterancesPage';
import { ModelType } from 'src/luis/types/ModelType';

/**
 * Represents the build page in the LUIS portal.
 */
export const BuildPane = () => {
    const { rootStore } = useGlobalState();

    return (
        <Switch>
            <PrivateRoute exact path={routeTable.luis.intentsPage()} component={IntentsPage} />
            <PrivateRoute exact path={routeTable.luis.intentPage()} component={renderIntentPage} />
            <PrivateRoute exact path={routeTable.luis.entitiesPage()} component={EntitiesPage} />
            <PrivateRoute exact path={routeTable.luis.entityPage()} component={renderEntityPage} />
            <PrivateRoute exact path={routeTable.luis.reviewUtterancesPage()} component={ReviewEndpointUtterancesPage} />
            <PrivateRoute exact path={routeTable.luis.phraseListsPage()} component={FeaturesPage} />
            <PrivateRoute exact path={routeTable.luis.patternsPage()} component={PatternsPage} />
            <PrivateRoute exact path={routeTable.luis.domainsPage()} component={PrebuiltDomainsPage} />
            <Redirect to={routeTable.luis.intentsPage(rootStore.currentLuisAppStore.id, '0.1')} />
        </Switch>
    );
};

/**
 * Routes to the utterance page with the given model id and model type.
 *
 * @param props The props received from the router.
 */
const renderIntentPage = (props: RouteComponentProps<{ intentId: string }>) => {
    const { params } = props.match;

    return <IntentPage model={{ id: params.intentId, typeId: ModelType.Intent }} />;
};

/**
 * Routes to the utterance page with the given model id and model type.
 *
 * @param props The props received from the router.
 */
const renderEntityPage = (props: RouteComponentProps<{ entityId: string }>) => {
    const { params } = props.match;

    return <EntityPage entityId={params.entityId} />;
};
